import { create } from 'zustand';
import { useEffect } from 'react';
import { cn } from '@/lib/cn';

type ToastKind = 'success' | 'error' | 'info';
interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastStore {
  toasts: ToastItem[];
  push: (kind: ToastKind, message: string) => void;
  dismiss: (id: number) => void;
}

let counter = 0;

const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (kind, message) =>
    set((s) => ({ toasts: [...s.toasts, { id: ++counter, kind, message }] })),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export const toast = {
  success: (m: string) => useToastStore.getState().push('success', m),
  error: (m: string) => useToastStore.getState().push('error', m),
  info: (m: string) => useToastStore.getState().push('info', m),
};

export function Toaster() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);
  return (
    <div className="pointer-events-none fixed inset-x-0 top-5 z-[60] flex flex-col items-center gap-2 px-4">
      {toasts.map((t) => (
        <ToastView key={t.id} item={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastView({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const isError = item.kind === 'error';
  useEffect(() => {
    // Errors linger longer (often a raw message the user needs to read) and can
    // always be dismissed manually.
    const id = setTimeout(onDismiss, isError ? 6500 : 4000);
    return () => clearTimeout(id);
  }, [onDismiss, isError]);

  const styles = {
    success: 'bg-makhe-forest text-makhe-bg',
    error: 'bg-makhe-plum text-makhe-bg',
    info: 'bg-makhe-ink text-makhe-bg',
  }[item.kind];

  return (
    <div
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
      className={cn(
        'pointer-events-auto inline-flex max-w-md items-center gap-3 rounded-full px-5 py-2.5 text-sm shadow-lift animate-rise',
        styles,
      )}
    >
      <span className="text-[10px] uppercase tracking-widest opacity-70">
        {item.kind === 'success' ? 'Done' : item.kind === 'error' ? 'Heads up' : 'Note'}
      </span>
      <span className="font-medium">{item.message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="-mr-1 ml-1 grid h-5 w-5 shrink-0 place-items-center rounded-full text-base leading-none opacity-70 hover:opacity-100"
      >
        ×
      </button>
    </div>
  );
}
